import axios, { AxiosInstance } from 'axios';
import type {
  RealTimePrice,
  EODData,
  IntradayData,
  SearchResult,
  ExchangeSymbol,
  ScreenerFilter,
  HeatmapResponse,
  NewsResponse,
  EconomicEventsResponse,
} from '../types';
import { CLIENT_HEADERS } from './api';

// Use backend API URL from environment or default to localhost
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8001';

const api: AxiosInstance = axios.create({
  baseURL: `${API_URL}/api/v1/market`,
  headers: {
    'Content-Type': 'application/json',
    ...CLIENT_HEADERS,
  },
});

interface HistoricalOptions {
  from?: string;
  to?: string;
  period?: 'd' | 'w' | 'm';
}

interface IntradayOptions {
  from?: number;
  to?: number;
}

interface TechnicalOptions {
  period?: number;
  fast_period?: number;
  slow_period?: number;
  signal_period?: number;
}

export interface TaAnalysis {
  recommendation: string;
  buy_count: number;
  sell_count: number;
  neutral_count: number;
  indicators: {
    RSI?: number;
    'MACD.macd'?: number;
    'MACD.signal'?: number;
    'Stoch.K'?: number;
    'Stoch.D'?: number;
    AO?: number;
    CCI20?: number;
    ADX?: number;
    'ADX+DI'?: number;
    'ADX-DI'?: number;
    EMA20?: number;
    EMA50?: number;
    EMA200?: number;
    SMA20?: number;
    SMA50?: number;
    SMA200?: number;
    ATR?: number;
    'BB.upper'?: number;
    'BB.lower'?: number;
    volume?: number;
    VWMA?: number;
  };
}

/**
 * Market Data Service - Routes through backend API with caching
 * Backend handles JSE price conversion and caching automatically
 */
export const eodhd = {
  /**
   * Get real-time or delayed quote for a symbol (cached by backend)
   */
  async getRealTimePrice(symbol: string): Promise<RealTimePrice> {
    try {
      const response = await api.get<RealTimePrice>(`/quote/${symbol}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching price for ${symbol}:`, error);
      throw error;
    }
  },

  /**
   * Get latest end-of-day data for a symbol
   * Note: Backend automatically handles fallback to EOD when real-time is unavailable
   */
  async getLatestEOD(symbol: string): Promise<RealTimePrice> {
    // Just use getRealTimePrice - backend handles EOD fallback
    return this.getRealTimePrice(symbol);
  },

  /**
   * Get end of day historical data (backend handles JSE price conversion)
   */
  async getHistoricalData(symbol: string, options: HistoricalOptions = {}): Promise<EODData[]> {
    try {
      const params: Record<string, string> = {};
      if (options.from) params.from = options.from;
      if (options.to) params.to = options.to;
      if (options.period) params.period = options.period;

      const response = await api.get<{ symbol: string; data: EODData[] }>(`/historical/${symbol}`, {
        params,
      });
      return response.data.data;
    } catch (error) {
      console.error(`Error fetching historical data for ${symbol}:`, error);
      throw error;
    }
  },

  /**
   * Get intraday historical data
   */
  async getIntradayData(
    symbol: string,
    interval: string = '5m',
    options: IntradayOptions = {}
  ): Promise<IntradayData[]> {
    try {
      const params: Record<string, string | number> = { interval };
      if (options.from !== undefined) params.from = options.from;
      if (options.to !== undefined) params.to = options.to;

      const response = await api.get<IntradayData[]>(`/intraday/${symbol}`, {
        params,
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching intraday data for ${symbol}:`, error);
      throw error;
    }
  },

  /**
   * Get stored 15-minute intraday bars for a JSE-listed symbol.
   * Bars are built from IRESS snapshots; prices in cents (ZAc).
   * Returns [] during cold start (no data collected yet).
   */
  async getJseBars(
    symbol: string,
    options: IntradayOptions = {}
  ): Promise<IntradayData[]> {
    try {
      const params: Record<string, number> = {};
      if (options.from !== undefined) params.from = options.from;
      if (options.to !== undefined) params.to = options.to;

      const response = await api.get<IntradayData[]>(`/jse-bars/${symbol}`, {
        params,
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching JSE bars for ${symbol}:`, error);
      return [];
    }
  },

  /**
   * Get fundamentals data for a stock
   */
  async getFundamentals(symbol: string): Promise<unknown> {
    try {
      const response = await api.get(`/fundamentals/${symbol}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching fundamentals for ${symbol}:`, error);
      throw error;
    }
  },

  /**
   * Get technical indicators
   */
  async getTechnicalIndicator(
    symbol: string,
    functionName: string,
    options: TechnicalOptions = {}
  ): Promise<unknown> {
    try {
      const params: Record<string, string | number> = { function: functionName };
      if (options.period !== undefined) params.period = options.period;
      if (options.fast_period !== undefined) params.fast_period = options.fast_period;
      if (options.slow_period !== undefined) params.slow_period = options.slow_period;
      if (options.signal_period !== undefined) params.signal_period = options.signal_period;

      const response = await api.get(`/technical/${symbol}`, { params });
      return response.data;
    } catch (error) {
      console.error(`Error fetching ${functionName} for ${symbol}:`, error);
      throw error;
    }
  },

  /**
   * Search for symbols
   */
  async searchSymbols(query: string, type: string | null = null): Promise<SearchResult[]> {
    try {
      const params: { q: string; type?: string } = { q: query };
      if (type) params.type = type;

      const response = await api.get<SearchResult[]>('/search', { params });
      return response.data;
    } catch (error) {
      console.error(`Error searching for ${query}:`, error);
      throw error;
    }
  },

  /**
   * Get exchange symbol list
   * Note: This endpoint may not be available through backend proxy
   */
  async getExchangeSymbols(exchange: string): Promise<ExchangeSymbol[]> {
    try {
      // This endpoint might need to be added to backend
      const response = await api.get<ExchangeSymbol[]>(`/exchange-symbols/${exchange}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching symbols for ${exchange}:`, error);
      throw error;
    }
  },

  /**
   * Get multiple quotes at once (cached by backend)
   */
  async getBulkQuotes(symbols: string[], cacheOnly = false): Promise<RealTimePrice[]> {
    try {
      const response = await api.get<RealTimePrice[]>('/quotes', {
        params: {
          symbols: symbols.join(','),
          ...(cacheOnly && { cache_only: true }),
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching bulk quotes:', error);
      throw error;
    }
  },

  /**
   * Get all JSE stock quotes at once (optimized batch endpoint with 2-minute caching)
   * Returns all JSE stocks from IRESS in a single call
   */
  async getAllJSEQuotes(): Promise<RealTimePrice[]> {
    try {
      const response = await api.get<RealTimePrice[]>('/jse/all');
      return response.data;
    } catch (error) {
      console.error('Error fetching all JSE quotes:', error);
      throw error;
    }
  },

  /**
   * Get live crypto prices
   */
  async getCryptoPrice(symbol: string): Promise<RealTimePrice> {
    try {
      // Ensure symbol has .CC suffix
      const cryptoSymbol = symbol.endsWith('.CC') ? symbol : `${symbol}.CC`;
      return this.getRealTimePrice(cryptoSymbol);
    } catch (error) {
      console.error(`Error fetching crypto price for ${symbol}:`, error);
      throw error;
    }
  },

  /**
   * Get forex rates
   */
  async getForexRate(pair: string): Promise<RealTimePrice> {
    try {
      // Ensure pair has .FOREX suffix
      const forexPair = pair.endsWith('.FOREX') ? pair : `${pair}.FOREX`;
      return this.getRealTimePrice(forexPair);
    } catch (error) {
      console.error(`Error fetching forex rate for ${pair}:`, error);
      throw error;
    }
  },

  /**
   * Get screener results
   * Note: This endpoint may not be available through backend proxy
   */
  async screenAssets(filters: ScreenerFilter = {}): Promise<unknown> {
    try {
      const response = await api.get('/screener', {
        params: filters,
      });
      return response.data;
    } catch (error) {
      console.error('Error running screener:', error);
      throw error;
    }
  },

  /**
   * Get market indices (cached by backend)
   */
  async getIndices(): Promise<RealTimePrice[]> {
    try {
      const indices = [
        'INDX.JSE', // JSE All Share Index
        'GSPC.INDX', // S&P 500
        'IXIC.INDX', // NASDAQ
        'DJI.INDX', // Dow Jones
      ];

      // Use bulk quotes for better performance
      return this.getBulkQuotes(indices);
    } catch (error) {
      console.error('Error fetching indices:', error);
      throw error;
    }
  },

  /**
   * Get financial news articles
   */
  async getNews(symbols?: string[], limit: number = 20, offset: number = 0): Promise<NewsResponse> {
    try {
      const params: Record<string, string | number> = { limit, offset };
      if (symbols && symbols.length > 0) {
        params.symbols = symbols.join(',');
      }

      const response = await api.get<NewsResponse>('/news', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching news:', error);
      throw error;
    }
  },

  /**
   * Get economic calendar events
   */
  async getEconomicEvents(
    fromDate?: string,
    toDate?: string,
    country?: string
  ): Promise<EconomicEventsResponse> {
    try {
      const params: Record<string, string> = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (country) params.country = country;

      const response = await api.get<EconomicEventsResponse>('/economic-events', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching economic events:', error);
      throw error;
    }
  },

  /**
   * Get market heatmap data (all sectors and stocks in single request)
   * Backend fetches all data in parallel and caches for 5 minutes
   */
  async getHeatmap(exchanges?: string[]): Promise<HeatmapResponse> {
    try {
      const params: Record<string, string> = {};
      if (exchanges && exchanges.length > 0) {
        params.exchanges = exchanges.join(',');
      }
      const response = await api.get<HeatmapResponse>('/heatmap', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching heatmap data:', error);
      throw error;
    }
  },

  async getTaAnalysis(symbol: string, interval = '1d'): Promise<TaAnalysis | null> {
    try {
      const response = await api.get<TaAnalysis>(`/ta/${symbol}`, { params: { interval } });
      return response.data;
    } catch (error) {
      console.error(`Error fetching TA analysis for ${symbol}:`, error);
      return null;
    }
  },
};

export default eodhd;
